import { LearnerPageChrome } from "@/pages/learners/page-chrome"
import { ActionLink } from "@/components/shared/action-buttons"
import { useTranslation } from "react-i18next"
import { useIndexProps } from "./page-props"
import { PageBody } from "@/components/ui/layout"
import { Ban, Eye, Inbox, ListTree, PlusCircle } from "lucide-react"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Empty,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
  EmptyHeader,
  EmptyContent,
} from "@/components/ui/empty"
import { Button } from "@/components/ui/button"
import { Resources } from "i18next"
import { buttonVariants } from "@/components/ui/button-variants"
import {
  ConfirmationDialog,
  ConfirmationDialogTrigger,
} from "@/components/shared/confirmation-dialog"
import { Badge } from "@/components/ui/badge"
import { Link } from "@/lib/page-rendering"

export default function Page() {
  const { t } = useTranslation("school_relief_listings", { keyPrefix: "index" })
  const { t: tSubjects } = useTranslation("subjects", { keyPrefix: "subjects" })
  const { t: tListingState } = useTranslation("school_relief_listings", {
    keyPrefix: "state",
  })
  const { relief_listings } = useIndexProps()

  return (
    <>
      <LearnerPageChrome
        action={
          <ActionLink
            variant="solid"
            href={"./relief_listings/new"}
            Icon={PlusCircle}
          >
            {t("create")}
          </ActionLink>
        }
      />
      <PageBody>
        <Card>
          <CardContent>
            {relief_listings.length === 0 ? (
              <Empty data-testid="relief-listings-empty">
                <EmptyHeader>
                  <EmptyMedia
                    variant="icon"
                    className="text-muted size-20 bg-white"
                  >
                    <Inbox className="size-16" />
                  </EmptyMedia>
                  <EmptyTitle>{t("empty_list_title")}</EmptyTitle>
                  <EmptyDescription>
                    {t("empty_list_description")}
                  </EmptyDescription>
                </EmptyHeader>
                <EmptyContent>
                  <Button asChild>
                    <Link href={"./relief_listings/new"}>{t("create")}</Link>
                  </Button>
                </EmptyContent>
              </Empty>
            ) : (
              <ul className="grid w-full grid-cols-1 gap-4 pt-6 sm:grid-cols-2">
                {relief_listings.map((relief_listing) => (
                  <li key={relief_listing.id}>
                    <Card data-testid="relief-listing-card">
                      <CardHeader>
                        <CardTitle>{relief_listing.title}</CardTitle>
                        <Badge data-testid="listing-state">
                          {tListingState(relief_listing.state)}
                        </Badge>
                      </CardHeader>
                      <CardContent>
                        <CardDescription className="text-base">
                          <dl className="grid w-fit grid-cols-2 gap-2 [&_dd]:font-medium">
                            <dt>{t("subject")}</dt>
                            <dd data-testid="subject">
                              {tSubjects(
                                relief_listing.subject_key as keyof Resources["subjects"]["subjects"],
                              )}
                            </dd>
                            <dt>{t("start_date")}</dt>
                            <dd data-testid="start_date">
                              {relief_listing.start_date}
                            </dd>
                            <dt>{t("end_date")}</dt>
                            <dd data-testid="end_date">
                              {relief_listing.end_date}
                            </dd>
                          </dl>
                        </CardDescription>
                      </CardContent>
                      <CardFooter className="flex flex-row flex-wrap gap-4">
                        <Button asChild>
                          <Link
                            href={`./relief_listings/${relief_listing.id}/requests`}
                          >
                            <ListTree />
                            {t("actions.requests")}
                          </Link>
                        </Button>
                        <Button asChild>
                          <Link
                            href={`/search/relief_listings/${relief_listing.id}`}
                          >
                            <Eye />
                            {t("actions.view_listing")}
                          </Link>
                        </Button>
                        {relief_listing.state === "open" && (
                          <ConfirmationDialog
                            description={t("actions.close_description")}
                            action={
                              <Link
                                className={buttonVariants({
                                  variant: "destructive",
                                })}
                                method="post"
                                href={`./relief_listings/${relief_listing.id}/close`}
                                as={Button}
                              >
                                {t("actions.close")}
                              </Link>
                            }
                          >
                            <ConfirmationDialogTrigger asChild>
                              <Button variant={"destructive-outline"}>
                                <Ban />
                                {t("actions.close")}
                              </Button>
                            </ConfirmationDialogTrigger>
                          </ConfirmationDialog>
                        )}
                      </CardFooter>
                    </Card>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </PageBody>
    </>
  )
}
